import { axiosLasta, refreshTokenTimeouts } from 'config';
import { refreshToken } from 'services/authService';
import { authActions } from 'store/auth/authSlice';
import { store } from 'store/store.js';
import { snackbarActions } from 'store/snackbars/snackbarsSlice';

export const buildResponseUnauthorizedErrorHandler = (errorMessage) => {
    return (error) => {
        const resStatus = error.response?.status;
        const isUnauthorized = resStatus === 401 || resStatus === 403;
        if (isUnauthorized) {
            store.dispatch(
                authActions.handleUnauthorized({
                    errorMessage: errorMessage || 'You dont have permission!',
                })
            );
            return Promise.resolve();
        }
        return Promise.reject(error);
    };
};

let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (error) => {
    const queue = pendingQueue;
    pendingQueue = [];
    queue.forEach(({ resolve, reject, request }) => {
        if (error) {
            reject(error);
        } else {
            resolve(axiosLasta.request(request));
        }
    });
};

const createTimeoutError = (timeoutMs) => {
    const timeoutError = new Error(`Refresh timeout after ${timeoutMs}ms`);
    timeoutError.isTimeout = true;
    return timeoutError;
};

const performRefreshWithTimeout = (timeoutMs) => {
    const refreshPromise = refreshToken();
    let timeoutId;

    const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(createTimeoutError(timeoutMs)), timeoutMs);
    });

    return Promise.race([refreshPromise.finally(() => clearTimeout(timeoutId)), timeoutPromise]);
};

export const handleResponseError = (error) => {
    if (error?.config?.handleResponseError) {
        return error?.config?.handleResponseError(error);
    }
    const resStatus = error.response?.status;
    const url = error?.config?.url || '';
    const isExternal = url.startsWith('https://birdseye-aiq-transaction-images.s3');
    const initiateLogout = (resStatus === 401 || resStatus === 403) && !isExternal;

    if (initiateLogout) {
        store.dispatch(authActions.handleUnauthorized());
        return;
    }
    return Promise.reject(error);
};

export const handleInterceptorResponseError = async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    const url = originalRequest?.url;

    if (status !== 401 && status !== 403) {
        return Promise.reject(error);
    }

    if (url === '/auth/refresh' || originalRequest?._retry) {
        store.dispatch(authActions.logoutStart());
        return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject, request: originalRequest });
        });
    }

    isRefreshing = true;
    try {
        const attemptRefresh = async () => {
            try {
                return await performRefreshWithTimeout(refreshTokenTimeouts.initial);
            } catch (firstError) {
                if (firstError?.isTimeout) {
                    store.dispatch(
                        snackbarActions.enqueueSnackbar({
                            message: 'Trying to reconnect, please wait…',
                            options: { variant: 'info' },
                        })
                    );
                    const result = await performRefreshWithTimeout(refreshTokenTimeouts.retry);
                    store.dispatch(
                        snackbarActions.enqueueSnackbar({
                            message: 'Reconnected successfully',
                            options: { variant: 'success' },
                        })
                    );
                    return result;
                }
                throw firstError;
            }
        };

        const refreshResponse = await attemptRefresh();
        if (refreshResponse?.status === 200) {
            const retried = axiosLasta.request(originalRequest);
            isRefreshing = false;
            flushQueue(null);
            return retried;
        }
        store.dispatch(authActions.logoutStart());
        flushQueue(new Error('Refresh failed'));
        return Promise.reject(error);
    } catch (refreshErr) {
        store.dispatch(authActions.logoutStart());
        flushQueue(refreshErr);
        return Promise.reject(refreshErr);
    } finally {
        isRefreshing = false;
    }
};
